import { Link, useNavigate } from "@tanstack/react-router";
import {
  type FunctionComponent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { FiArrowLeft } from "react-icons/fi";
import {
  AnnounceRequestError,
  type PublicAnnouncement,
} from "@/features/announce/api/announceClient";
import { AnnouncementBody } from "@/features/announce/components/AnnouncementBody";
import {
  AnnouncementLevelBadge,
  AnnouncementTimestamps,
} from "@/features/announce/components/AnnouncementContent";
import { useAnnouncementStatus } from "@/features/announce/hooks/useAnnouncementStatus";
import {
  useAnnouncement,
  useDesktopModalAnnouncements,
} from "@/features/announce/hooks/useAnnouncements";
import {
  areAllFetchedAnnouncementsRead,
  getAnnouncementReadThroughTimestamp,
  getFetchedAnnouncementsReadThroughRevision,
} from "@/features/announce/lib/announcementReadReceipt";
import {
  type AnnouncementEvent,
  isAnnouncementEvent,
} from "@/features/announce/mediators/announcementEvents";
import {
  type AnnouncementModalEffect,
  type AnnouncementModalView,
  createAnnouncementModalMediator,
  isAnnouncementModalEvent,
} from "@/features/announce/mediators/announcementModalMediator";
import { AnnouncementItemPresenter } from "@/features/announce/presenters/AnnouncementItemPresenter";
import { hasLinkModifierKey } from "@/shared/lib/keyboardAccessibility";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";
import { LoadingScreen } from "@/shared/ui/feedback";
import { Button } from "@/shared/ui/form";
import { Modal } from "@/shared/ui/overlay/Modal";

interface DesktopAnnouncementModalProps {
  initialAnnouncementId?: number;
  isOpen: boolean;
  onClose: () => void;
}

interface AnnouncementModalListItem {
  readonly announcement: PublicAnnouncement;
  readonly isRead: boolean;
}

export const DesktopAnnouncementModal: FunctionComponent<
  DesktopAnnouncementModalProps
> = ({ initialAnnouncementId, isOpen, onClose }) => {
  const query = useDesktopModalAnnouncements();
  const navigate = useNavigate();
  const hasCurrentRevisionList = query.dataUpdatedAt > 0;
  const announcements = query.data?.announcements;
  const listedInitialAnnouncement = hasCurrentRevisionList
    ? announcements?.find((item) => item.id === initialAnnouncementId)
    : undefined;
  const hasSettledCurrentRevisionList = hasCurrentRevisionList || query.isError;
  const shouldFetchInitialAnnouncement =
    isOpen &&
    initialAnnouncementId !== undefined &&
    listedInitialAnnouncement === undefined &&
    hasSettledCurrentRevisionList;
  const initialQuery = useAnnouncement(
    shouldFetchInitialAnnouncement ? initialAnnouncementId : null,
  );
  const initialAnnouncement =
    initialQuery.dataUpdatedAt > 0 ? initialQuery.data : undefined;
  const isInitialAnnouncementLoading =
    initialAnnouncementId !== undefined &&
    (!hasSettledCurrentRevisionList ||
      (shouldFetchInitialAnnouncement && initialQuery.isFetching));
  const readThroughRevision =
    hasCurrentRevisionList && query.data
      ? getFetchedAnnouncementsReadThroughRevision(
          query.data.announcements,
          query.data.articleUnreadRevision,
        )
      : null;
  const { isAnnouncementRead } = useAnnouncementStatus();
  const effectInputs = useRef({ onClose, query, initialQuery, navigate });
  useLayoutEffect(() => {
    effectInputs.current = { onClose, query, initialQuery, navigate };
  });
  const mediator = useMemo(
    () =>
      createAnnouncementModalMediator((effect: AnnouncementModalEffect) => {
        const { onClose, query, initialQuery, navigate } = effectInputs.current;
        switch (effect.type) {
          case "close":
            onClose();
            return;
          case "retry-list":
            void query.refetch();
            return;
          case "retry-initial":
            void initialQuery.refetch();
            return;
          case "open-page":
            if (effect.announcementId === undefined) {
              void navigate({ to: "/announcements" });
            } else {
              void navigate({
                to: "/announcements/$announcementId",
                params: { announcementId: String(effect.announcementId) },
              });
            }
        }
      }),
    [],
  );
  useHierarchyLifetime(mediator);
  const state = useMediatorSnapshot(mediator);
  const handler = useCallback(
    (event: { readonly type: string }): "handled" | "unhandled" => {
      if (!isAnnouncementEvent(event)) return "unhandled";
      if (!isAnnouncementModalEvent(event)) return "unhandled";
      const result = mediator.dispatch(event);
      return result.status === "handled" ? "handled" : "unhandled";
    },
    [mediator],
  );
  const node = useComponentNode(handler);
  const lastReadAnnouncementRef = useRef<PublicAnnouncement | null>(null);
  const dispatchEvent = useCallback(
    (event: AnnouncementEvent): void => {
      node.dispatch(event);
    },
    [node],
  );

  useEffect(() => {
    mediator.dispatch({
      type: "announcement/modal-initial-synced",
      announcement: initialAnnouncement,
      error: initialQuery.error,
      initialAnnouncementId,
      isLoading: isInitialAnnouncementLoading,
      listedAnnouncement: listedInitialAnnouncement,
    });
  }, [
    initialAnnouncement,
    initialAnnouncementId,
    initialQuery.error,
    isInitialAnnouncementLoading,
    listedInitialAnnouncement,
    mediator,
  ]);

  useEffect(() => {
    if (
      !isOpen ||
      state.view.name !== "list" ||
      announcements?.length !== 0 ||
      readThroughRevision === null
    ) {
      return;
    }
    dispatchEvent({
      type: "announcement/read-through-requested",
      publishedAt: null,
      articleUnreadRevision: readThroughRevision,
    });
  }, [
    announcements,
    dispatchEvent,
    isOpen,
    readThroughRevision,
    state.view.name,
  ]);

  useEffect(() => {
    if (state.view.name !== "detail") {
      lastReadAnnouncementRef.current = null;
      return;
    }
    const announcement = state.view.announcement;
    if (lastReadAnnouncementRef.current === announcement) return;
    lastReadAnnouncementRef.current = announcement;
    const allFetchedRead = areAllFetchedAnnouncementsRead(
      announcements,
      announcement.id,
      isAnnouncementRead,
    );
    dispatchEvent({ type: "announcement/read-requested", announcement });
    if (allFetchedRead) {
      dispatchEvent({
        type: "announcement/read-through-requested",
        publishedAt: getAnnouncementReadThroughTimestamp(
          announcements ?? [],
          announcement,
        ),
        articleUnreadRevision:
          readThroughRevision === null
            ? null
            : Math.max(
                readThroughRevision,
                announcement.article_unread_revision ?? 0,
              ),
      });
    }
  }, [
    announcements,
    isAnnouncementRead,
    dispatchEvent,
    readThroughRevision,
    state.view,
  ]);

  const listItems = announcements?.map((announcement) => ({
    announcement,
    isRead: isAnnouncementRead(announcement),
  }));
  return (
    <ComponentScope node={node}>
      <DesktopAnnouncementModalView
        error={query.error}
        initialAnnouncementId={initialAnnouncementId}
        isInitialAnnouncementLoading={isInitialAnnouncementLoading}
        isLoading={query.isLoading}
        isOpen={isOpen}
        listItems={listItems}
        onEvent={(event): void => {
          node.dispatch(event);
        }}
        view={state.view}
      />
    </ComponentScope>
  );
};

interface DesktopAnnouncementModalViewProps {
  readonly error: unknown;
  readonly initialAnnouncementId?: number;
  readonly isInitialAnnouncementLoading: boolean;
  readonly isLoading: boolean;
  readonly isOpen: boolean;
  readonly listItems: readonly AnnouncementModalListItem[] | undefined;
  readonly onEvent: (event: AnnouncementEvent) => void;
  readonly view: AnnouncementModalView;
}

export const DesktopAnnouncementModalView: FunctionComponent<
  DesktopAnnouncementModalViewProps
> = ({
  error,
  initialAnnouncementId,
  isInitialAnnouncementLoading,
  isLoading,
  isOpen,
  listItems,
  onEvent,
  view,
}) => {
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef(new Map<number, HTMLButtonElement>());
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (view.name === "detail") backButtonRef.current?.focus();
      if (view.name === "list" && view.focusAnnouncementId !== undefined) {
        itemRefs.current.get(view.focusAnnouncementId)?.focus();
      }
    });
    return (): void => cancelAnimationFrame(frame);
  }, [view]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={(): void => onEvent({ type: "announcement/modal-closed" })}
      position="top"
      title="告知"
      headerActions={
        <Link
          to="/announcements"
          onClick={(event): void => {
            const nativeNavigation =
              event.button !== 0 || hasLinkModifierKey(event);
            if (!nativeNavigation) event.preventDefault();
            onEvent({
              type: "announcement/modal-page-requested",
              nativeNavigation,
            });
          }}
          className="inline-flex min-h-11 items-center px-2 text-sm text-primary underline"
        >
          一覧ページ
        </Link>
      }
    >
      {view.name === "detail" ? (
        <AnnouncementModalDetail
          announcement={view.announcement}
          backButtonRef={backButtonRef}
          onEvent={onEvent}
        />
      ) : view.name === "initialDetailError" ? (
        <AnnouncementModalError
          error={view.error}
          onEvent={onEvent}
          retryType="announcement/detail-retried"
        />
      ) : (
        <AnnouncementModalList
          error={error}
          isLoading={
            isLoading ||
            (initialAnnouncementId !== undefined &&
              isInitialAnnouncementLoading)
          }
          itemRefs={itemRefs.current}
          listItems={listItems}
          onEvent={onEvent}
        />
      )}
    </Modal>
  );
};

const AnnouncementModalList: FunctionComponent<{
  error: unknown;
  isLoading: boolean;
  itemRefs: Map<number, HTMLButtonElement>;
  listItems: readonly AnnouncementModalListItem[] | undefined;
  onEvent: (event: AnnouncementEvent) => void;
}> = ({ error, isLoading, itemRefs, listItems, onEvent }) => {
  if (isLoading && listItems === undefined) {
    return <LoadingScreen message="告知を読み込み中..." />;
  }
  if (error && listItems === undefined) {
    return (
      <AnnouncementModalError
        error={error}
        onEvent={onEvent}
        retryType="announcement/list-retried"
      />
    );
  }
  if (listItems?.length === 0) {
    return (
      <p className="px-4 py-12 text-center text-muted-foreground">
        現在公開されている告知はありません。
      </p>
    );
  }
  return (
    <ol className="space-y-2 p-4">
      {listItems?.map(({ announcement, isRead }) => (
        <li key={announcement.id}>
          <AnnouncementItemPresenter>
            {(emit): React.ReactNode => (
              <article className="rounded-lg border border-border">
                <button
                  ref={(element): void => {
                    if (element) itemRefs.set(announcement.id, element);
                    else itemRefs.delete(announcement.id);
                  }}
                  type="button"
                  onClick={(): void =>
                    emit({ type: "announcement/detail-opened", announcement })
                  }
                  className="grid min-h-11 w-full grid-cols-[minmax(0,1fr)_auto_auto] items-start gap-3 rounded-t-lg p-3 text-left transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                >
                  <b className="min-w-0 break-words text-foreground">
                    {announcement.title}
                  </b>
                  {!isRead && (
                    <strong className="rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-primary-foreground">
                      NEW
                    </strong>
                  )}
                  <AnnouncementLevelBadge level={announcement.level} />
                </button>
                <footer className="border-t border-border px-3 py-2">
                  <AnnouncementTimestamps announcement={announcement} />
                </footer>
              </article>
            )}
          </AnnouncementItemPresenter>
        </li>
      ))}
    </ol>
  );
};

const AnnouncementModalDetail: FunctionComponent<{
  announcement: PublicAnnouncement;
  backButtonRef: React.RefObject<HTMLButtonElement | null>;
  onEvent: (event: AnnouncementEvent) => void;
}> = ({ announcement, backButtonRef, onEvent }) => (
  <article className="p-4">
    <button
      ref={backButtonRef}
      type="button"
      onClick={(): void =>
        onEvent({
          type: "announcement/modal-list-requested",
          focusAnnouncementId: announcement.id,
        })
      }
      className="mb-4 inline-flex min-h-11 items-center gap-2 text-primary underline"
    >
      <FiArrowLeft aria-hidden="true" />
      告知一覧に戻る
    </button>
    <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border pb-4">
      <h3 className="min-w-0 flex-1 break-words text-xl font-bold text-foreground">
        {announcement.title}
      </h3>
      <AnnouncementLevelBadge level={announcement.level} />
      <AnnouncementTimestamps
        announcement={announcement}
        className="basis-full"
      />
    </header>
    <section aria-labelledby="announcement-modal-body-title" className="py-5">
      <h4 id="announcement-modal-body-title" className="sr-only">
        告知本文
      </h4>
      <AnnouncementBody html={announcement.body_html} />
    </section>
    <Link
      to="/announcements/$announcementId"
      params={{ announcementId: String(announcement.id) }}
      onClick={(event): void => {
        const nativeNavigation =
          event.button !== 0 || hasLinkModifierKey(event);
        if (!nativeNavigation) event.preventDefault();
        onEvent({
          type: "announcement/modal-page-requested",
          nativeNavigation,
          announcementId: announcement.id,
        });
      }}
      className="inline-flex min-h-11 items-center text-primary underline"
    >
      この告知をページで開く
    </Link>
  </article>
);

const AnnouncementModalError: FunctionComponent<{
  error: unknown;
  onEvent: (event: AnnouncementEvent) => void;
  retryType: "announcement/detail-retried" | "announcement/list-retried";
}> = ({ error, onEvent, retryType }) => {
  const message =
    error instanceof AnnounceRequestError && error.kind === "timeout"
      ? "告知の読み込みが時間内に完了しませんでした。"
      : "告知を読み込めませんでした。通信状態を確認してください。";
  return (
    <section
      aria-label="告知の読み込みエラー"
      role="alert"
      className="flex min-h-[200px] flex-col items-center justify-center gap-4 px-4 text-center text-destructive"
    >
      <p>
        {message}
        <br />
        しばらくしてから再試行してください。
      </p>
      <Button onClick={(): void => onEvent({ type: retryType })}>再試行</Button>
    </section>
  );
};
